import { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import {
    LayoutDashboard, Users, UserPlus, Activity, Settings,
    LogOut, Menu, X, ChevronDown, Bell, Shield, Search,
    BarChart3, AlertCircle, HelpCircle, ChevronsLeft,
    Home, CreditCard, Megaphone, User, Wifi, WifiOff
} from 'lucide-react';
import webSocketService from '@/services/websocket';

const AdminLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user: admin, logout } = useAuthStore();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
    const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);

    const navigation = [
        { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
        { name: 'Users', href: '/admin/users', icon: Users },
        { name: 'Create User', href: '/admin/users/create', icon: UserPlus },
        { name: 'Activity Log', href: '/admin/activity', icon: Activity },
        { name: 'Settings', href: '/admin/settings', icon: Settings },
        { name: 'Announcements', href: '/admin/announcements', icon: Megaphone },
    ];

    // WebSocket connection management
    useEffect(() => {
        const socket = webSocketService.connect();
        if (socket) {
            setIsWebSocketConnected(webSocketService.isConnected());

            const handleConnect = () => setIsWebSocketConnected(true);
            const handleDisconnect = () => setIsWebSocketConnected(false);

            webSocketService.on('connect', handleConnect);
            webSocketService.on('disconnect', handleDisconnect);

            return () => {
                webSocketService.off('connect', handleConnect);
                webSocketService.off('disconnect', handleDisconnect);
            };
        }
    }, []);

    const isActive = (href) => {
        return location.pathname === href || location.pathname.startsWith(href + '/');
    };

    const handleLogout = () => {
        logout();
        navigate('/admin/login');
    };

    return (
        <div className="min-h-screen bg-gray-50 flex">
            {/* Desktop Sidebar */}
            <div className={`hidden lg:flex lg:flex-shrink-0 transition-all duration-300 ${
                sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'
            }`}>
                <div className="flex flex-col w-full">
                    <div className="flex flex-col h-full bg-gray-900">
                        {/* Logo */}
                        <div className="flex items-center h-16 px-4 bg-gray-800">
                            <Link to="/admin/dashboard" className="flex items-center">
                                <Shield className="h-8 w-8 text-yellow-500" />
                                {!sidebarCollapsed && (
                                    <span className="ml-3 text-xl font-semibold text-white">
                    Admin Portal
                  </span>
                                )}
                            </Link>
                        </div>

                        {/* Navigation */}
                        <nav className="flex-1 px-2 py-4 space-y-1">
                            {navigation.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <Link
                                        key={item.name}
                                        to={item.href}
                                        className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                                            isActive(item.href)
                                                ? 'bg-gray-800 text-white'
                                                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                        }`}
                                        title={sidebarCollapsed ? item.name : ''}
                                    >
                                        <Icon className={`h-5 w-5 ${
                                            sidebarCollapsed ? 'mx-auto' : 'mr-3'
                                        }`} />
                                        {!sidebarCollapsed && item.name}
                                    </Link>
                                );
                            })}
                        </nav>

                        {/* User Section */}
                        <div className="flex-shrink-0 flex border-t border-gray-800 p-4">
                            {sidebarCollapsed ? (
                                <button
                                    onClick={() => setSidebarCollapsed(false)}
                                    className="flex items-center justify-center w-full text-gray-400 hover:text-white"
                                >
                                    <ChevronsLeft className="h-5 w-5 rotate-180" />
                                </button>
                            ) : (
                                <div className="flex items-center w-full">
                                    <div className="flex-shrink-0">
                                        <div className="h-8 w-8 rounded-full bg-yellow-500 flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-900">
                        {admin?.name?.charAt(0).toUpperCase() || 'A'}
                      </span>
                                        </div>
                                    </div>
                                    <div className="ml-3 flex-1">
                                        <p className="text-sm font-medium text-white">
                                            {admin?.name || 'Admin'}
                                        </p>
                                        <p className="text-xs text-gray-400 truncate">
                                            {admin?.email}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setSidebarCollapsed(true)}
                                        className="ml-3 text-gray-400 hover:text-white"
                                    >
                                        <ChevronsLeft className="h-5 w-5" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Sidebar */}
            {mobileMenuOpen && (
                <div className="lg:hidden fixed inset-0 flex z-40">
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setMobileMenuOpen(false)} />

                    <div className="relative flex-1 flex flex-col max-w-xs w-full bg-gray-900">
                        <div className="absolute top-0 right-0 -mr-12 pt-2">
                            <button
                                onClick={() => setMobileMenuOpen(false)}
                                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                            >
                                <X className="h-6 w-6 text-white" />
                            </button>
                        </div>

                        <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                            <div className="flex items-center px-4">
                                <Shield className="h-8 w-8 text-yellow-500" />
                                <span className="ml-3 text-xl font-semibold text-white">
                  Admin Portal
                </span>
                            </div>
                            <nav className="mt-5 px-2 space-y-1">
                                {navigation.map((item) => {
                                    const Icon = item.icon;
                                    return (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            onClick={() => setMobileMenuOpen(false)}
                                            className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${
                                                isActive(item.href)
                                                    ? 'bg-gray-800 text-white'
                                                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                            }`}
                                        >
                                            <Icon className="mr-4 h-6 w-6" />
                                            {item.name}
                                        </Link>
                                    );
                                })}
                            </nav>
                        </div>

                        <div className="flex-shrink-0 flex border-t border-gray-800 p-4">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className="h-10 w-10 rounded-full bg-yellow-500 flex items-center justify-center">
                    <span className="text-base font-medium text-gray-900">
                      {admin?.name?.charAt(0).toUpperCase() || 'A'}
                    </span>
                                    </div>
                                </div>
                                <div className="ml-3">
                                    <p className="text-base font-medium text-white">
                                        {admin?.name || 'Admin'}
                                    </p>
                                    <p className="text-sm text-gray-400">
                                        {admin?.email}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <div className="flex-1 flex flex-col">
                {/* Top Header */}
                <header className="bg-white shadow-sm border-b border-gray-200">
                    <div className="px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between items-center h-16">
                            <div className="flex items-center">
                                <button
                                    onClick={() => setMobileMenuOpen(true)}
                                    className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                                >
                                    <Menu className="h-6 w-6" />
                                </button>

                                {/* Search */}
                                <div className="hidden md:block ml-4 lg:ml-0">
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Search className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                            placeholder="Search..."
                                        />
                                    </div>
                                </div>
                                {/* WebSocket Status */}
                                <div className="hidden sm:flex items-center ml-4">
                                    {isWebSocketConnected ? (
                                        <div className="flex items-center text-green-600" title="Real-time updates connected">
                                            <Wifi className="h-4 w-4" />
                                            <span className="ml-1 text-xs font-medium">Live</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center text-gray-400" title="Real-time updates disconnected">
                                            <WifiOff className="h-4 w-4" />
                                            <span className="ml-1 text-xs font-medium">Offline</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center space-x-4">
                                {/* Quick Links */}
                                <Link
                                    to="/"
                                    className="hidden sm:flex items-center text-sm text-gray-500 hover:text-gray-700"
                                >
                                    <Home className="h-4 w-4 mr-1" />
                                    Main Site
                                </Link>

                                {/* Notifications */}
                                <button className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors">
                                    <Bell className="h-5 w-5" />
                                    <span className="absolute top-1.5 right-1.5 h-2 w-2 bg-red-500 rounded-full"></span>
                                </button>

                                {/* Profile Dropdown */}
                                <div className="relative">
                                    <button
                                        onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                                        className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                    >
                                        <div className="h-8 w-8 rounded-full bg-yellow-500 flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-900">
                        {admin?.name?.charAt(0).toUpperCase() || 'A'}
                      </span>
                                        </div>
                                        <ChevronDown className="hidden sm:block ml-2 h-4 w-4 text-gray-400" />
                                    </button>

                                    {/* Dropdown Menu */}
                                    {profileDropdownOpen && (
                                        <>
                                            <div
                                                className="fixed inset-0 z-10"
                                                onClick={() => setProfileDropdownOpen(false)}
                                            />
                                            <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                                                <div className="py-1">
                                                    <div className="px-4 py-2 border-b border-gray-100">
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {admin?.name || 'Admin'}
                                                        </p>
                                                        <p className="text-xs text-gray-500 truncate">
                                                            {admin?.email}
                                                        </p>
                                                    </div>

                                                    <Link
                                                        to="/admin/account"
                                                        onClick={() => setProfileDropdownOpen(false)}
                                                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    >
                                                        <User className="h-4 w-4 mr-3 text-gray-400" />
                                                        Account Settings
                                                    </Link>

                                                    <Link
                                                        to="/admin/settings"
                                                        onClick={() => setProfileDropdownOpen(false)}
                                                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    >
                                                        <Settings className="h-4 w-4 mr-3 text-gray-400" />
                                                        System Settings
                                                    </Link>

                                                    <Link
                                                        to="/admin/help"
                                                        onClick={() => setProfileDropdownOpen(false)}
                                                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    >
                                                        <HelpCircle className="h-4 w-4 mr-3 text-gray-400" />
                                                        Help & Support
                                                    </Link>

                                                    <div className="border-t border-gray-100">
                                                        <button
                                                            onClick={handleLogout}
                                                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                        >
                                                            <LogOut className="h-4 w-4 mr-3 text-gray-400" />
                                                            Sign out
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page Content */}
                <main className="flex-1">
                    <Outlet />
                </main>

                {/* Footer */}
                <footer className="bg-white border-t border-gray-200">
                    <div className="px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                                <span>&copy; 2024 Digital Business Card Admin. All rights reserved.</span>
                            </div>
                            <div className="flex items-center space-x-4 text-sm">
                                <span className="text-gray-500">Version 1.0.0</span>
                                <Link to="/admin/activity" className="text-gray-500 hover:text-gray-700">
                                    Activity Log
                                </Link>
                                <Link to="/admin/settings" className="text-gray-500 hover:text-gray-700">
                                    Settings
                                </Link>
                            </div>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default AdminLayout;